import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { usePrevious } from '_hooks';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import DateField from 'components/atomic/fields/DateField';
import MaskField from 'components/atomic/fields/MaskField';
import SelectField from 'components/atomic/fields/SelectField';
import Text from 'components/atomic/layouts/Text';

const StyledForm = styled(Form)`
  width: 100%;
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const PersonalInfoForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    setValue,
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevDefaultValues = usePrevious(defaultValues);
  const values = useWatch({ control });
  const prevValues = usePrevious(values);

  const [emailVerifying, setEmailVerifying] = useState(false);
  const [spouseEmailVerifying, setSpouseEmailVerifying] = useState(false);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (!isEqual(prevValues, values)) {
      if (values[FIELD_KEYS.spouse] === 'no' && !values[FIELD_KEYS.dependents].length) {
        setValue(FIELD_KEYS.filingSingle, 'single');
      }
      if (values[FIELD_KEYS.spouse] === 'no' && !!values[FIELD_KEYS.dependents].length) {
        setValue(FIELD_KEYS.filingSingle, 'household');
      }
      if (values[FIELD_KEYS.spouse] === 'no') {
        setValue(FIELD_KEYS.marriedFilingSeparately, null);
      }
      if (values[FIELD_KEYS.spouse] === 'yes') {
        setValue(FIELD_KEYS.marriedFilingSeparately, 'jointly');
        setValue(FIELD_KEYS.filingSingle, null);
      }
    }
  }, [prevValues, values]);

  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset(defaultValues);
    }
  }, [prevDefaultValues, defaultValues]);

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.firstName)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.lastName)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <MaskField {...getFieldProps(FIELD_KEYS.ssn)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <DateField
            {...getFieldProps(FIELD_KEYS.dateOfBirth, {
              validationOptions: {
                values,
              },
            })}
            icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
            dateFormat="MM/dd/yyyy"
            bottomSpace={theme.spaces.regular}
            control={control}
          />
        </Col>
        <Col xs={12} md={6}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.email, { validationOptions: { setProcessing: setEmailVerifying } })}
            loading={emailVerifying}
            feedback={emailVerifying ? 'Verifying...' : undefined}
            control={control}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
        <Col xs={12} md={6}>
          <MaskField {...getFieldProps(FIELD_KEYS.phone)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.address1)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.zip)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <BaseField {...getFieldProps(FIELD_KEYS.city)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={6}>
          <SelectField {...getFieldProps(FIELD_KEYS.state)} control={control} bottomSpace={theme.spaces.regular * 3} />
        </Col>
        <Col xs={12} md={3}>
          <SelectField {...getFieldProps(FIELD_KEYS.spouse)} control={control} bottomSpace={theme.spaces.regular} />
        </Col>
        <Col xs={12} md={9} />
        {values[FIELD_KEYS.spouse] === 'yes' && (
          <>
            <Col xs={12} md={6}>
              <BaseField
                {...getFieldProps(FIELD_KEYS.spouseFirstName)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <BaseField
                {...getFieldProps(FIELD_KEYS.spouseLastName)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <MaskField
                {...getFieldProps(FIELD_KEYS.spouseSsn)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <DateField
                {...getFieldProps(FIELD_KEYS.spouseDateOfBirth, {
                  validationOptions: {
                    values,
                  },
                })}
                icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
                dateFormat="MM/dd/yyyy"
                bottomSpace={theme.spaces.regular}
                control={control}
              />
            </Col>
            <Col xs={12} md={6}>
              <BaseField
                {...getFieldProps(FIELD_KEYS.spouseEmail, {
                  validationOptions: { setProcessing: setSpouseEmailVerifying },
                })}
                loading={spouseEmailVerifying}
                feedback={spouseEmailVerifying ? 'Verifying...' : undefined}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <MaskField
                {...getFieldProps(FIELD_KEYS.spousePhone)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <BaseField
                {...getFieldProps(FIELD_KEYS.spouseAddress1)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <BaseField
                {...getFieldProps(FIELD_KEYS.spouseZip)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <BaseField
                {...getFieldProps(FIELD_KEYS.spouseCity)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
            <Col xs={12} md={6}>
              <SelectField
                {...getFieldProps(FIELD_KEYS.spouseState)}
                control={control}
                bottomSpace={theme.spaces.regular}
              />
            </Col>
          </>
        )}
        <Col xs={12} md={3} />
        <Col xs={12} md={6}>
          <Button
            type="submit"
            disabled={emailVerifying || spouseEmailVerifying || submitDisabled}
            color={theme.colors.black}
          >
            Save Information
          </Button>
        </Col>
        <Col xs={12} md={3} />
      </Row>

      {(loading || emailVerifying || spouseEmailVerifying) && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Processing...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </StyledForm>
  );
};

PersonalInfoForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default PersonalInfoForm;
